import { buildPreviewProxyUrl, fetchPreviewBlob } from '../../project-essential/services/api';
import { saveBlobDownload } from './saveBlobDownload';

function mimeFromFilename(filename: string): string | null {
  const ext = filename.match(/\.([a-z0-9]{1,8})$/i)?.[1]?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'zip':
      return 'application/zip';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'ppt':
      return 'application/vnd.ms-powerpoint';
    case 'pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    default:
      return null;
  }
}

function isGenericMime(contentType: string | null | undefined): boolean {
  const ct = (contentType || '').toLowerCase().split(';')[0].trim();
  return (
    !ct ||
    ct === 'application/octet-stream' ||
    ct === 'binary/octet-stream' ||
    ct === 'application/force-download' ||
    ct === 'application/download'
  );
}

function ensureBlobMime(blob: Blob, mime: string | null | undefined): Blob {
  if (!mime || (!isGenericMime(blob.type) && blob.type === mime)) return blob;
  if (!isGenericMime(blob.type) && blob.type) return blob;
  return new Blob([blob], { type: mime });
}

function urlNeedsDownloadProxy(rawUrl: string): boolean {
  if (!rawUrl || rawUrl === '#') return false;
  try {
    const parsed = new URL(rawUrl, window.location.href);
    if (parsed.origin === window.location.origin) return false;
    return Boolean(buildPreviewProxyUrl(rawUrl));
  } catch {
    return false;
  }
}

async function fetchDownloadBlob(
  url: string,
  mimeHint: string | null,
): Promise<{ blob: Blob; contentType: string | null }> {
  if (urlNeedsDownloadProxy(url)) {
    const { objectUrl, contentType } = await fetchPreviewBlob(url);
    try {
      const response = await fetch(objectUrl);
      if (!response.ok) throw new Error(`Blob read failed: ${response.status}`);
      const blob = await response.blob();
      return {
        blob: ensureBlobMime(
          blob,
          mimeHint || (isGenericMime(contentType) ? null : contentType),
        ),
        contentType,
      };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);
  const blob = await response.blob();
  const contentType = response.headers.get('content-type');
  return {
    blob: ensureBlobMime(blob, mimeHint || (isGenericMime(contentType) ? null : contentType)),
    contentType,
  };
}

/**
 * Fetch a remote file and save it under `filename`. Uses WebView-safe delivery
 * (share sheet / short-lived HTTPS URL) inside native app wrappers.
 */
export async function triggerForceDownload(url: string, filename: string): Promise<void> {
  const safeName = filename.trim() || 'download.bin';
  const mimeHint = mimeFromFilename(safeName);

  try {
    const { blob } = await fetchDownloadBlob(url, mimeHint);
    await saveBlobDownload(blob, safeName, mimeHint || blob.type);
  } catch {
    // Last resort: navigate with download hint (works on desktop web).
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    a.rel = 'noopener noreferrer';
    if (!urlNeedsDownloadProxy(url)) a.target = '_blank';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    a.remove();
  }
}
